import youbora from 'youboralib';
import 'youbora-adapter-chromecastcaf';
import { getUser } from './user';
import { getGvpData, getKeyValues } from './config';
import { getToken } from './gvp';
import * as logger from '../utils/logger';
import { getQuality } from '../utils/medias';
import { toMilliSeconds } from './seek';
import { youboraIntegrationLevel, marks } from '../constants';

const log = logger.create('Youbora');
const playerManager = cast.framework.CastReceiverContext.getInstance().getPlayerManager();
const SESSION_ID_PRECISION = 1e10;
const EN_VIVO_DELAY = 50000;
let ybPlugin,
  options = {};

/**
 * Sends real JoinTime as a customDimension.
 */
export function sendRealJoinTime() {
  try {
    const realJoinTime = Math.round(
      performance.measure('realJoinTime', marks.realJoinTime).duration
    );
    log.info('Adding real JoinTime to Youbora', realJoinTime);
    ybPlugin.setOptions({
      'extraparam.12': realJoinTime
    });
  } catch (e) {
    log.info('Error ignored updating youbora params with real JoinTime');
  }
}

/**
 * Set initial configuration and options
 */
export function initYoubora() {
  // If mobile sends version, initialization will happen each after load request using that data.
  // Same session or zapping must keep same instance.
  if (!ybPlugin) {
    const { instanceId, senderDeviceType, version } = getGvpData();
    ybPlugin = new youbora.Plugin({
      accountCode: process.env.YOUBORA_ACCOUNT_CODE,
      enabled: true,
      username: getUser().id
    });
    setOptions({
      'content.transactionCode': getToken(),
      'device.code': 'Chromecast',
      'extraparam.1': instanceId,
      'extraparam.3': `${process.env.YOUBORA_ACCOUNT_CODE}`,
      'extraparam.4': 'chromecast',
      'extraparam.5': `${process.env.VERSION}`,
      'extraparam.10': version ? `${senderDeviceType}_${version}` : undefined,
      'extraparam.12': undefined,
      'extraparam.13': getUser().userGroups.join(',')
    });
    setYouboraAdapterChromecast();
  }
}

/**
 * Inits join time.
 */
export function fireInit() {
  ybPlugin.getAdapter().fireInit();
  // Set JoinTime start time.
  try {
    const measures = performance.getEntriesByName(marks.joinTime);
    const lastMark = measures[measures.length - 1] ?? measures[0];

    ybPlugin.initChrono.startTime = Math.round(performance.timeOrigin + lastMark.startTime);
    log.info('Overriding JoinTime start time to', ybPlugin.initChrono.startTime);
  } catch (e) {
    log.error('Error overriding JoinTime start time.');
  }
}

/**
 * Set content options on start
 * @param {Object} videoData Content data.
 */
export function startSetup(videoData) {
  if (
    (getKeyValues().integrationLevel === youboraIntegrationLevel.both ||
      getKeyValues().integrationLevel === videoData.type) &&
    !videoData.isTrailer
  ) {
    ybPlugin.enable();
    const data = {
      ...videoData,
      metadata: {
        ...videoData.metadata,
        device: {
          type: getGvpData().deviceType,
          model: 'chromecast'
        },
        sessionId: parseInt(Math.random() * SESSION_ID_PRECISION, 10),
        version: `${process.env.VERSION_EXT}`
      }
    };
    ybPlugin.setOptions({
      'parse.cdnNode': true,
      'parse.cdnNode.list': ['Telefonica', 'Akamai'],
      'content.title': data.title,
      'content.program': data.program,
      'content.duration': data.duration,
      'content.resource': data.resource,
      'content.isLive': data.isLive,
      'content.metadata': data.metadata,
      'content.id': data.metadata.contentId,
      'extraparam.2': getQuality(data.quality),
      'extraparam.7': data.commercializationType,
      'extraparam.8': data.itemType,
      waitForMetadata: true,
      pendingMetadata: ['param12']
    });
    ybPlugin.setOptions(getOptions());
  } else {
    ybPlugin.disable();
  }
}

/**
 * Create Youbora's Chromecast adapter
 */
function setYouboraAdapterChromecast() {
  if (typeof youbora.adapters.ChromecastCAF !== 'undefined') {
    try {
      const chromecastAdapter = youbora.adapters.ChromecastCAF.extend({ getLatency });
      ybPlugin.setAdapter(new chromecastAdapter(playerManager));
      log.info('setYouboraAdapterChromecast');
    } catch (e) {
      fireError({ msg: 'setYouboraAdapterChromecast exception' });
      log.error('setYouboraAdapterChromecast exception', e);
    }
  }
}

/**
 * Fire error in Youbora plugin
 * @param {Object} error Error.
 */
export function fireError(error) {
  ybPlugin.fireError(error);
  log.info('fireError set');
}

/**
 * Store option fields
 * @param {Object} newOptions
 */
function setOptions(newOptions) {
  options = newOptions;
  log.info('setOptions', getOptions());
}

/**
 * Retrieve option fields
 * @return {Object} Options
 */
function getOptions() {
  return options;
}

/**
 * Creates a performance mark for realJoinTime. This measures since the last interaction of the user
 * and value is included in the customDimension.12.
 */
export function createRealJoinTimeMark() {
  log.info('RealJoinTime mark created on', new Date());
  performance.mark(marks.realJoinTime);
}

/**
 * Creates a performance mark for joinTime. This value overrides JoinTime value bacause plugin
 * does not work as expected.
 */
export function createJoinTimeMark() {
  log.info('JoinTime mark created on', new Date());
  performance.mark(marks.joinTime);
}

/**
 * Removes youbora plugin.
 */
export function destroy() {
  if (ybPlugin) {
    ybPlugin.removeAdapter();
    ybPlugin = null;
  }
}

/**
 * Obtains delay between server time and local time.
 * @returns {number} Delay (ms).
 */
function getTime() {
  const delayMarks = performance.getEntriesByName('uaDelay', 'mark');
  return delayMarks[delayMarks.length - 1].detail.delay;
}

/**
 * Obtains player latency. Only called by Youbora in live content.
 * @returns {number|null} Current video time while considered live edge (ms).
 */
function getLatency() {
  const latency =
    Date.now() +
    getTime() -
    toMilliSeconds(playerManager.getAbsoluteTimeForMediaTime(playerManager.getCurrentTimeSec()));
  if (latency <= EN_VIVO_DELAY) {
    return latency;
  } else {
    return null;
  }
}
